import type { CSSProperties } from 'react'

export type BackgroundPreset = `white` | `warm` | `cool` | `paper`
export type FontPreset = `system` | `classic` | `modern`

export type Appearance = {
  accentColor: string
  backgroundPreset: BackgroundPreset
  fontPreset: FontPreset
}

/** A small set of neutral presets rather than a free color picker on the page background,
 *  which is what every form tool the office manager has already used offers. */
export const BACKGROUND_PRESETS: Record<BackgroundPreset, string> = {
  white: `#ffffff`,
  warm: `#fdfaf5`,
  cool: `#f7fafc`,
  paper: `#f5f4f0`,
}

export const FONT_PRESETS: Record<FontPreset, string> = {
  system: `ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif`,
  classic: `Georgia, "Times New Roman", serif`,
  modern: `"Inter", ui-sans-serif, system-ui, sans-serif`,
}

export const DEFAULT_APPEARANCE: Appearance = {
  accentColor: `#1d4ed8`,
  backgroundPreset: `white`,
  fontPreset: `system`,
}

const LIGHT_FG = `#111827`
const DARK_FG = `#ffffff`
/** WCAG relative luminance midpoint. Above this the accent is light and needs dark text. */
const LUMINANCE_THRESHOLD = 0.5

function parseHex(input: string): [number, number, number] | undefined {
  const hex = input.trim().replace(/^#/, ``)
  const full = hex.length === 3 ? hex.replace(/./g, (c) => c + c) : hex
  if (!/^[0-9a-f]{6}$/i.test(full)) return undefined
  return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)]
}

function channelLuminance(value: number): number {
  const c = value / 255
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

/** Derived, never configured. A practice cannot choose an unreadable pairing. */
export function accentForeground(accentColor: string): string {
  const rgb = parseHex(accentColor)
  if (!rgb) return DARK_FG
  const [r, g, b] = rgb
  const luminance = 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b)
  return luminance > LUMINANCE_THRESHOLD ? LIGHT_FG : DARK_FG
}

export function appearanceStyle(appearance: Appearance): CSSProperties & Record<string, string> {
  return {
    '--form-accent': appearance.accentColor,
    '--form-accent-fg': accentForeground(appearance.accentColor),
    '--form-bg': BACKGROUND_PRESETS[appearance.backgroundPreset],
    '--form-font': FONT_PRESETS[appearance.fontPreset],
  }
}
